import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, {
  ChangeEvent,
  forwardRef,
  KeyboardEvent,
  MouseEvent,
  RefObject,
  useCallback,
  useEffect,
  useId,
  useState,
} from 'react';
import {
  List,
  RowComponentProps,
  useDynamicRowHeight,
  useListRef,
} from 'react-window';
import { useBackgroundColor } from '../../../utils';
import { Expand, Filter, Tick } from '../../icon';
import { Stack } from '../../stack/Stack';
import { BaseFormProps, StyledBase } from '../base/Base';
import { FormField, FormFieldProps } from '../formField/FormField';
import { Grid } from '../../grid/Grid';
import tokens from './tokens';

export interface FilterInputProps extends BaseFormProps, FormFieldProps {
  options: Options[];
  onChange: (...event: any[]) => void;
  allowExternalUpdates?: boolean;
  constrained?: boolean;
  disableFilter?: boolean;
  dynamicRowHeights?: boolean;
  externalFilter?: (value: string) => void;
  externalLoading?: boolean;
  labelNote?: string;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  showFilterIcon?: boolean;
  value?: string | number;
}

export interface Options {
  name: string;
  value: string | number;
}

interface RowProps {
  activeValue: string | number | null;
  dynamicRowHeights?: boolean;
  filteredOptions: Options[];
  handleOptionClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  id: string;
  selectedValue: string | number | null;
}

interface StyledFilterInputProps {
  showFilterIcon?: boolean;
}

const StyledFilterInput = styled(Grid)<StyledFilterInputProps>`
  ${({ showFilterIcon }) => css`
    position: relative;

    > button:first-of-type {
      ${showFilterIcon &&
      css`
        padding-left: 56px;
      `}
    }
  `}
`;

const StyledToggleButton = styled.button`
  ${({ theme: { space } }) => css`
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    padding: 0 ${space.small};

    svg {
      transition: 0.2s rotate;
    }

    &[aria-label='Close'] svg {
      rotate: 180deg;
    }
  `}
`;

const StyledOptions = styled.div`
  ${({ theme: { space, colors, borderRadii, layers } }) => css`
    position: absolute;
    right: 0;
    top: calc(100% + ${space.xxSmall});
    width: 100%;
    background: ${colors.utilityWhite};
    border-radius: ${borderRadii[
      tokens.optionsBorderRadius as keyof typeof borderRadii
    ]};
    border: solid 1px
      ${colors[tokens.optionsBorderColor as keyof typeof colors]};
    box-shadow: 0 0 0 1px
      ${colors[tokens.optionsBorderColor as keyof typeof colors]};
    z-index: ${layers.filterInput};
    padding-left: ${space.xSmall};
    padding-right: ${space.medium};

    transition: opacity 0s 0.1s;

    &[inert='true'] {
      opacity: 0;
      transition: opacity 0s;
    }
  `}
`;

const StyledOptionsInner = styled(List<RowProps>)`
  ${({ theme: { space, colors, borderRadii } }) => css`
    margin-block: ${space.small};
    scrollbar-color: ${colors[
        tokens.optionsBackgroundColor as keyof typeof colors
      ]}
      ${colors.utilityBackground};

    &::-webkit-scrollbar-track {
      border-radius: ${borderRadii.medium};
      background-color: ${colors.utilityBackground};
    }
    &::-webkit-scrollbar {
      width: ${space.xxSmall};
      background-color: ${colors.utilityBackground};
    }
    &::-webkit-scrollbar-thumb {
      border-radius: ${borderRadii.medium};
      background-color: ${colors[
        tokens.optionsBackgroundColor as keyof typeof colors
      ]};
    }
  `}
`;

const StyledOption = styled.div<{ dynamicRowHeights?: boolean }>`
  ${({ theme: { borderRadii, colors, space }, dynamicRowHeights }) => css`
    display: flex;
    padding-block: ${space.xxxSmall};
    padding-inline: ${space.xxSmall};

    button {
      position: relative;
      display: flex;
      align-items: center;
      flex-grow: 1;
      border-radius: ${borderRadii.medium};
      padding: ${space.xxSmall} ${space.small};
      border-radius: ${borderRadii.medium};
      text-align: left;
      cursor: default;

      ${!dynamicRowHeights &&
      css`
        width: 100%;

        span {
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: clip;
          width: 100%;
        }
      `}

      // Make room for and around the ✓
      &[aria-selected='true'] span {
        width: calc(100% - ${space.xxSmall} - 24px);
      }

      svg {
        position: absolute;
        right: ${space.small};
      }

      ${useBackgroundColor(
        tokens.optionsBackgroundColorUnselected as keyof typeof colors,
      )}

      &:hover {
        background-color: ${colors[
          tokens.optionsHoverBackgroundColor as keyof typeof colors
        ]};
        color: ${colors.utilityText};
        cursor: pointer;
        ${tokens.optionsHoverUnderline &&
        css`
          text-decoration: underline;
        `}
      }

      &[data-active='true'] {
        ${useBackgroundColor(
          tokens.optionsBackgroundColor as keyof typeof colors,
        )}
      }
    }
  `}
`;

const StyledFilterIcon = styled(Filter)`
  ${({ theme: { space, colors } }) => css`
    position: absolute;
    left: ${space.small};
    top: 50%;
    transform: translateY(-50%);
    color: ${colors[tokens.filterIconColor as keyof typeof colors]};
  `}
`;

function RowComponent({
  activeValue,
  dynamicRowHeights,
  filteredOptions,
  handleOptionClick,
  id,
  index,
  selectedValue,
  style,
}: RowComponentProps<RowProps>) {
  const option = filteredOptions[index];

  return (
    <StyledOption
      key={`${id}-${index}`}
      style={{
        ...style,
      }}
      id={`${id}-${index}`}
      data-index={index}
      dynamicRowHeights={dynamicRowHeights}
    >
      <button
        type="button"
        role="option"
        aria-selected={option.value === selectedValue}
        data-active={option.value === activeValue}
        onClick={handleOptionClick}
        tabIndex={-1}
      >
        <span>{option.name}</span>
        {option.value === selectedValue && (
          <Tick aria-hidden="true" size="small" />
        )}
      </button>
    </StyledOption>
  );
}

export const FilterInput = forwardRef<HTMLSelectElement, FilterInputProps>(
  (
    {
      label,
      id,
      invalid,
      valid,
      defaultValue,
      value,
      placeholder,
      required,
      errorMessage,
      hideLabel,
      disabled,
      hintText,
      optional,
      extraInformation,
      options,
      disableFilter,
      externalFilter,
      allowExternalUpdates,
      large,
      onChange,
      constrained,
      labelNote,
      onFocus,
      showFilterIcon,
      dynamicRowHeights,
      ...rest
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState<string>(() =>
      !defaultValue || typeof defaultValue !== 'string' ? '' : defaultValue,
    );
    const [showOptions, setShowOptions] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState<Options[]>(options);
    const [activeValue, setActiveValue] = useState<string | number | null>(
      options.find((option) => option.name === defaultValue)?.value || null,
    );
    const [selectedValue, setSelectedValue] = useState<string | number | null>(
      options.find((option) => option.name === defaultValue)?.value || null,
    );
    const listRef = useListRef(null);
    const dynamicRowHeight = useDynamicRowHeight({ defaultRowHeight: 48 });
    const rowHeight = dynamicRowHeights ? dynamicRowHeight : 48;
    const filterInputOptionsContainerId = useId();

    const getCurrentActiveIndex = () =>
      filteredOptions.findIndex((option) => option.value === activeValue) ?? -1;

    const getCurrentFilteredIndex = useCallback(
      () =>
        filteredOptions.findIndex((option) => option.value === selectedValue) ??
        -1,
      [filteredOptions, selectedValue],
    );

    const setActiveAt = (indexModifier: number): void => {
      if (filteredOptions.length === 0) return;

      const activeIndex = getCurrentActiveIndex();

      if (
        activeIndex + indexModifier > filteredOptions.length - 1 ||
        activeIndex + indexModifier < 0
      ) {
        return;
      }

      setActiveValue(filteredOptions[activeIndex + indexModifier].value);

      listRef.current?.scrollToRow({
        index: activeIndex + indexModifier,
        behavior: 'smooth',
      });
    };

    useEffect(() => {
      if (showOptions) {
        setTimeout(() => {
          const selectedIndex = getCurrentFilteredIndex();

          listRef.current?.scrollToRow({
            index: selectedIndex === -1 ? 0 : selectedIndex,
            align: 'smart',
          });
        }, 20); // Show for a frame or two before scrolling to allow react-window calculations
      }
    }, [showOptions, getCurrentFilteredIndex, listRef]);

    // Single click handler for all options, uses data-index
    const handleOptionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      const option =
        filteredOptions[Number(e.currentTarget.parentElement?.dataset.index)];
      if (!option) return;
      setInputValue(option.name);
      setSelectedValue(option.value);
      setActiveValue(option.value);
      setShowOptions(false);
      setFilteredOptions(options);
      onChange?.(option.value);
    };

    // if external filter is used
    // we need to set  the filtered input value
    useEffect(() => {
      if (externalFilter || allowExternalUpdates) {
        setFilteredOptions(options);
      }
    }, [externalFilter, inputValue, options, allowExternalUpdates]);

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      // if external API call is needed
      if (externalFilter) {
        externalFilter(value);
        setShowOptions(true);
        setInputValue(value);
        return;
      }

      // or local filtering
      setInputValue(value);
      onChange('');
      setSelectedValue(null);
      if (value.length >= 3) {
        const optionList = options.filter((option) =>
          option.name.toLowerCase().includes(value.toLowerCase()),
        );
        setFilteredOptions(optionList);
        setShowOptions(true);
      } else {
        setFilteredOptions(options);
        setShowOptions(false);
      }
    };

    const selectActive = () => {
      const activeIndex = getCurrentActiveIndex();

      if (activeIndex === -1) return;

      setInputValue(filteredOptions[activeIndex].name);
      setSelectedValue(filteredOptions[activeIndex].value);
      setActiveValue(filteredOptions[activeIndex].value);
      onChange(filteredOptions[activeIndex].value);
    };

    const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();

        if (showOptions) {
          selectActive();
          setShowOptions(false);
          setFilteredOptions(options);
        } else {
          setShowOptions(true);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowOptions(false);
        setActiveValue(selectedValue ?? null);
      } else if (e.key === 'ArrowDown') {
        if (showOptions) {
          e.preventDefault();
          setActiveAt(+1);
        }
      } else if (e.key === 'ArrowUp') {
        if (showOptions) {
          e.preventDefault();
          setActiveAt(-1);
        }
      }
    };

    const handleKeyDown = (
      // Both the expand button and non-search inputs are buttons, but TypeScript
      // doesn't follow the as prop to know it's HTMLButtonElement not HTMLInputElement
      e: KeyboardEvent<HTMLButtonElement | HTMLInputElement | HTMLDivElement>,
    ) => {
      if (e.key === ' ' || e.key === 'Enter') {
        // Space and Enter opens or selects + closes
        e.preventDefault();

        if (!showOptions) {
          setShowOptions(true);
        } else {
          selectActive();
          setShowOptions(false);
          setFilteredOptions(options);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowOptions(false);
        setActiveValue(selectedValue ?? null);
      } else if (e.key === 'ArrowDown') {
        if (showOptions) {
          e.preventDefault();
          setActiveAt(+1);
        }
      } else if (e.key === 'ArrowUp') {
        if (showOptions) {
          e.preventDefault();
          setActiveAt(-1);
        }
      }
    };

    const handleToggleClick = (
      // Both the expand button and non-search inputs are buttons, but TypeScript
      // doesn't follow the as prop to know it's HTMLButtonElement not HTMLInputElement
      e: MouseEvent<HTMLButtonElement | HTMLInputElement>,
    ) => {
      e.preventDefault();

      if (!showOptions) {
        setShowOptions(true);
      } else {
        setShowOptions(false);
        setActiveValue(selectedValue ?? null);
      }
    };

    return (
      <FormField
        fieldAs="fieldset"
        label={label}
        htmlFor={id}
        invalid={invalid}
        errorMessage={errorMessage}
        hideLabel={hideLabel}
        hintText={hintText}
        // disabled={disabled}
        optional={optional}
        extraInformation={extraInformation}
        required={required}
        {...rest}
      >
        <Stack gap="xxSmall">
          {labelNote && <span>{labelNote}</span>}
          <StyledFilterInput
            desktopColumns={constrained ? '2' : 'none'}
            showFilterIcon={showFilterIcon}
          >
            {disableFilter ? (
              <StyledBase
                onClick={handleToggleClick}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                valid={valid}
                invalid={invalid}
                id={id}
                ref={ref as unknown as RefObject<HTMLInputElement>}
                as="button"
                type="button"
                leftAligned
                large={large}
                onFocus={onFocus}
                aria-expanded={showOptions}
                aria-controls={filterInputOptionsContainerId}
              >
                {inputValue || placeholder}
              </StyledBase>
            ) : (
              <StyledBase
                onChange={handleOnChange}
                value={inputValue}
                placeholder={placeholder}
                onKeyDown={handleInputKeyDown}
                valid={valid}
                invalid={invalid}
                id={id}
                ref={ref as unknown as RefObject<HTMLInputElement>}
                active={showOptions}
                autoComplete="off"
                large={large}
                onFocus={onFocus}
                role="combobox"
                aria-expanded={showOptions}
                aria-controls={filterInputOptionsContainerId}
              />
            )}

            <StyledToggleButton
              type="button"
              onClick={handleToggleClick}
              onKeyDown={handleKeyDown}
              id={`${id}-action`}
              aria-label={showOptions ? 'Close' : 'Expand'}
            >
              <Expand aria-hidden="true" />
            </StyledToggleButton>
            <StyledOptions
              id={filterInputOptionsContainerId}
              inert={showOptions ? undefined : 'true'}
              role="group"
              onKeyDown={handleKeyDown}
            >
              {filteredOptions.length > 0 ? (
                <StyledOptionsInner
                  id={`${id}-options`}
                  rowCount={filteredOptions.length}
                  rowHeight={rowHeight}
                  overscanCount={4}
                  rowComponent={RowComponent}
                  rowProps={{
                    activeValue,
                    dynamicRowHeights,
                    filteredOptions,
                    handleOptionClick,
                    id,
                    selectedValue,
                  }}
                  listRef={listRef}
                  style={{ maxHeight: `${48 * 6}px` }}
                />
              ) : (
                <StyledOption
                  key={`${id}-no-results`}
                  id={`${id}-no-results`}
                  data-test="no-results"
                  data-index={-1}
                  style={{
                    marginInline: 8,
                    marginBlock: 24,
                  }}
                >
                  No results found
                </StyledOption>
              )}
            </StyledOptions>
            {showFilterIcon && (
              <StyledFilterIcon color={tokens.filterIconColor} />
            )}
          </StyledFilterInput>
        </Stack>
      </FormField>
    );
  },
);

FilterInput.displayName = 'FilterInput';
